import { createContext, useContext, useEffect, useRef, useSyncExternalStore } from 'react'
import type { Platform } from '../core/bindings.ts'
import type { MenuHost, MenuModel, MenuTable } from '../core/menu.ts'
import type { PaletteEntry, Screen } from '../core/palette.ts'
import type { Command } from '../core/registry.ts'
import type { Invocation } from '../core/trainer.ts'
import type { Shell, ShellState } from './shell.ts'
import type { LookupState } from './lookups.ts'
import type { FindState } from './find.ts'

/// The overlays' part of a shell's state.
export interface ChromeState {
  palette: boolean
  menu: string | null
  shortcuts: boolean
}

/// The registry as the overlays read it, whichever shell's it is. A command's `when` takes that
/// shell, so it is typed for none here.
export interface ChromeRegistry {
  chord(id: string, platform: Platform): string | null
  all(): readonly Pick<Command<never, MenuTable>, 'id' | 'title' | 'menu' | 'help' | 'when'>[]
}

/// What the menu bar, the palette and the shortcuts sheet read from the shell around them. The
/// shell's Shell is one, and the aligner's page (src/align/app/shell.ts) provides its own.
export interface Chrome {
  readonly platform: Platform
  readonly menuHost: MenuHost<MenuTable>
  readonly registry: ChromeRegistry
  readonly paletteScreen: Screen
  readonly store: { subscribe(listener: () => void): () => void; get(): ChromeState }
  set(patch: Partial<ChromeState>): void
  command(id: string, via?: Invocation): Promise<unknown> | null
  shownChord(id: string): string | null
  paletteMounted(input: HTMLInputElement | null): string
  paletteClosed(): void
  paletteEntries(): PaletteEntry[]
  recentCommands(): string[]
  menuModel(): MenuModel<string>
  openMenu(menu: string): void
  closeMenu(restore?: boolean): void
  invokeMenu(id: string): Promise<unknown> | null
}

export const ShellContext = createContext<Chrome | null>(null)

/// The shell's own components, under the App that provides a Shell.
export function useShell(): Shell {
  return useChrome() as Shell
}

/// The components both pages draw.
export function useChrome(): Chrome {
  const shell = useContext(ShellContext)
  if (!shell) throw new Error('no Shell in context')
  return shell
}

/// The overlays' state; the component re-renders when any of the shell's state changes.
export function useChromeState(): ChromeState {
  const shell = useChrome()
  return useSyncExternalStore(shell.store.subscribe, shell.store.get, shell.store.get)
}

/// The shell's state; the component re-renders when any of it changes. The same snapshot serves
/// a render to markup, which the node tests use.
export function useShellState(): ShellState {
  const shell = useShell()
  return useSyncExternalStore(shell.store.subscribe, shell.store.get, shell.store.get)
}

/// The handler a form's root takes on its input events, which says the form holds what the person
/// typed or chose, so a new build waits for it (docs/design/offline-mode.md, decision 3). The hold
/// goes when the form is no longer `shown` and when the component goes.
export function useHeld(place: string, shown = true): () => void {
  const shell = useShell()
  useEffect(() => {
    if (!shown) shell.releaseInput(place)
    return () => shell.releaseInput(place)
  }, [shell, place, shown])
  return () => shell.holdInput(place)
}

/// The active Segment's three answers; the component re-renders when one lands or clears.
export function useLookups(): LookupState {
  const shell = useShell()
  return useSyncExternalStore(shell.lookups.store.subscribe, shell.lookups.store.get)
}

/// The find's state; the component re-renders when its result, marks or dialog change.
export function useFind(): FindState {
  const shell = useShell()
  return useSyncExternalStore(shell.find.store.subscribe, shell.find.store.get)
}

/// Whether a mouse event is the pointer moving. Chromium and WebKit both fire a mousemove at a
/// resting pointer when a list scrolls or lays out under it, which would move the selection the
/// arrow keys made to the row under the mouse, so a list selects on a pointer that moved. The
/// first event has nothing to be compared with and only records where the pointer rests.
export function usePointerMoved(): (e: { clientX: number; clientY: number }) => boolean {
  const pointer = useRef<[number, number] | null>(null)
  return e => {
    const at: [number, number] = [e.clientX, e.clientY]
    const was = pointer.current
    pointer.current = at
    return was !== null && (was[0] !== at[0] || was[1] !== at[1])
  }
}
